/**
 * 1- or 2-level tree node used by `TreeSelectMenu`.
 * Depth > 2 not supported: rendering only displays 2 levels.
 */
export interface TreeNode<T extends string = string> {
  code: T
  label: string
  children?: TreeNode<T>[]
}

export const getLeafCodes = <T extends string>(node: TreeNode<T>): T[] => {
  if (!node.children || node.children.length === 0) {
    return [node.code]
  }
  return node.children.flatMap((child) => getLeafCodes(child))
}

export type NodeState = "checked" | "indeterminate" | "unchecked"

export const getNodeState = <T extends string>(
  node: TreeNode<T>,
  value: T[],
): NodeState => {
  const leaves = getLeafCodes(node)
  const selectedSet = new Set(value)
  const selectedCount = leaves.filter((code) => selectedSet.has(code)).length

  if (selectedCount === 0) return "unchecked"
  if (selectedCount === leaves.length) return "checked"
  return "indeterminate"
}

/**
 * Computes the new `value` after clicking a node (leaf or parent).
 *
 * Logic:
 * - If the node is "checked" (all its leaves selected): remove all its leaves
 *   from the value.
 * - Otherwise ("unchecked" or "indeterminate"): add all its missing leaves.
 *   The "indeterminate" -> "checked" transition completes the selection.
 *
 * For a pure leaf, `getLeafCodes` returns `[node.code]` so the logic reduces
 * to a simple add/remove of the code.
 *
 * The function is pure: returns a new array, does not mutate `value`.
 */
export const computeNextValue = <T extends string>(
  node: TreeNode<T>,
  currentValue: T[],
): T[] => {
  const leavesCode = getLeafCodes(node)
  const state = getNodeState(node, currentValue)
  const leafSet = new Set(leavesCode)

  if (state === "checked") {
    return currentValue.filter((code) => !leafSet.has(code))
  }

  const valueSet = new Set(currentValue)
  for (const leafCode of leavesCode) {
    valueSet.add(leafCode)
  }
  return Array.from(valueSet)
}

/**
 * Normalizes a string for case- and accent-insensitive comparison.
 *
 * Steps:
 * 1. normalize("NFD"): decomposes each accented character into a base letter
 *    + combining diacritical mark. E.g. "é" (1 codepoint U+00E9) becomes
 *    "e" + "◌́" (U+0065 + U+0301).
 * 2. replace(/\p{M}/gu, ""): strips all marks (Unicode category "Mark").
 *    \p{M} targets accents, cedillas, umlauts, etc. The "u" flag enables
 *    Unicode mode (required for \p{...}), "g" replaces all occurrences.
 * 3. toLowerCase(): ignores case.
 *
 * Example: "Diététique" -> "Die◌te◌́tique" (NFD) -> "Dietetique" -> "dietetique"
 */
const normalize = (s: string): string =>
  s.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase()

export const getParentsToAutoExpand = <T extends string>(
  filtered: TreeNode<T>[],
  term: string,
): Set<T> => {
  const set = new Set<T>()
  if (term.trim() === "") return set
  for (const node of filtered) {
    if (node.children && node.children.length > 0) {
      set.add(node.code)
    }
  }
  return set
}

export const filterTree = <T extends string>(
  options: TreeNode<T>[],
  term: string,
): TreeNode<T>[] => {
  const trimmed = term.trim()
  if (trimmed === "") return options

  const needle = normalize(trimmed)
  const matchesLabel = (label: string): boolean => normalize(label).includes(needle)

  const result: TreeNode<T>[] = []
  for (const node of options) {
    if (matchesLabel(node.label)) {
      result.push(node)
      continue
    }
    if (!node.children || node.children.length === 0) continue
    const matchingChildren = node.children.filter((child) =>
      matchesLabel(child.label),
    )
    if (matchingChildren.length > 0) {
      result.push({ ...node, children: matchingChildren })
    }
  }
  return result
}
